import { useAuth } from '@/context/AuthContext';
import { Users, Check } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

/**
 * Compact dropdown to switch the active child. Hidden when family has 0-1 child.
 * Includes a "Manage Family" link at the bottom.
 */
export default function ChildSelector() {
  const { user, activeChildName, setActiveChild } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const children = user?.children || [];
  if (children.length < 2) return null;

  const active = children.find(c => c.name === activeChildName) || children[0];

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="child-selector-toggle"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-fern-50 hover:bg-fern-100 text-fern-dark transition-colors text-sm font-medium"
      >
        <Users className="w-4 h-4" />
        <span className="hidden sm:inline">{active.name}</span>
        <span className="text-xs text-fern/70 hidden sm:inline">· Y{active.year_level}</span>
      </button>
      {open && (
        <div data-testid="child-selector-menu" className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden z-50">
          <div className="px-4 py-2 border-b border-slate-100">
            <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Active Child</p>
          </div>
          {children.map(c => {
            const isActive = c.name === active.name;
            return (
              <button
                key={c.name}
                data-testid={`child-option-${c.name}`}
                onClick={() => { setActiveChild(c.name); setOpen(false); }}
                className={`w-full flex items-center justify-between px-4 py-2.5 text-left text-sm hover:bg-fern-50 transition-colors ${isActive ? 'bg-fern-50 text-fern-dark font-medium' : 'text-slate-700'}`}
              >
                <span className="flex items-center gap-2">
                  <span>{c.name}</span>
                  <span className="text-xs text-slate-400">Y{c.year_level}</span>
                </span>
                {isActive && <Check className="w-4 h-4 text-fern" />}
              </button>
            );
          })}
          <Link
            to="/family"
            onClick={() => setOpen(false)}
            data-testid="manage-family-link"
            className="block w-full px-4 py-2.5 text-sm text-moana hover:bg-moana/5 border-t border-slate-100 transition-colors"
          >
            Manage Family →
          </Link>
        </div>
      )}
    </div>
  );
}
